/* The plate's GRADE (docs/PRODUCT-LAB.md § Grade): a card's photograph graded where it hangs, never in the file.
   A plate may carry exposure, brightness, contrast, highlights, shadows, whites, blacks, temperature, tint, vibrance,
   saturation, hue and sharpness (−100…100, except sharpness 0…100) beside its framing and grain. By default only the PIECE is graded: its black plate is found and
   left exactly as it was ("ignoring the black background completely"). `whole: true` grades the whole picture.

   Finding the piece: the dark field that reaches the picture's edge is the plate, and so is any large dark field the
   piece encloses (a ring's hole); a dark detail inside the piece (a letter's recess, black leather) is small and stays
   the piece's. The mask is feathered a few pixels so the grade meets the black without a seam. A picture whose edge is
   not dark (a lifestyle photograph) has no plate to keep, and is graded whole.

   The chain: linear-light exposure and white balance → tonal controls → hue, vibrance and saturation → sharpness. Grain is the
   plate's own overlay (--plate-grain), drawn above the graded picture, so it is always last; its overlay blend leaves
   black black, so it never lifts the plate either.

   One WebGL context for the whole page; each graded picture becomes a blob the <img> shows, so the plate's framing
   (its transform, object-fit) applies to it untouched. The original src is kept, and a grade taken off restores it.

   A FRAME is graded too (docs/COVER-LAB.md § Grade): a cover, a door, a tile — the photograph it shows, with the same
   chain and the same two ways (the black kept, or the whole picture). Its grade is the copy layer's <key>_grade, printed
   as data-frame-grade by snippets/lab-slot on every theme, published or not; the Cover Lab grades it live. */
(() => {
  if (window.ggGrade) return;
  const KEYS = ['exposure', 'brightness', 'contrast', 'highlights', 'shadows', 'whites', 'blacks', 'temperature', 'tint', 'vibrance', 'saturation', 'hue', 'sharpness'];
  const isGraded = (g) => !!g && KEYS.some((k) => Math.abs(+g[k] || 0) >= 0.5);
  const clean = (g) => {
    if (!g || typeof g !== 'object') return null;
    const out = {};
    for (const k of KEYS) { const v = Math.round(+g[k] || 0); const lo = k === 'sharpness' ? 0 : -100; if (v) out[k] = Math.max(lo, Math.min(100, v)); }
    if (g.whole) out.whole = true;
    return isGraded(out) ? out : null;
  };

  /* ---- the mask: 1 on the piece, 0 on its black plate ---- */
  const MASK_MAX = 320;
  function maskOf(img) {
    const w0 = img.naturalWidth, h0 = img.naturalHeight; const k = Math.min(1, MASK_MAX / Math.max(w0, h0));
    const w = Math.max(8, Math.round(w0 * k)), h = Math.max(8, Math.round(h0 * k));
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(img, 0, 0, w, h);
    const px = x.getImageData(0, 0, w, h).data; const n = w * h;
    const L = new Float32Array(n); const alpha = new Uint8Array(n);
    for (let i = 0; i < n; i++) { L[i] = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255; alpha[i] = px[i * 4 + 3]; }
    // the plate's own darkness, read off the picture's edge
    const edge = []; for (let i = 0; i < w; i++) { edge.push(L[i], L[(h - 1) * w + i]); } for (let j = 0; j < h; j++) { edge.push(L[j * w], L[j * w + w - 1]); }
    edge.sort((a, b) => a - b); const plate = edge[edge.length >> 1];
    const m = new Float32Array(n).fill(1);
    if (plate > 0.2) return { w, h, m, plate: false };             // no black plate: the whole picture is the piece
    const T = Math.max(0.055, plate + 0.05);
    // dark fields, each labelled; a field touching the edge, or a large one (a ring's hole), is plate
    const lab = new Int32Array(n).fill(-1); const q = new Int32Array(n); let fields = 0;
    const bigField = n * 0.012;
    for (let s = 0; s < n; s++) {
      if (lab[s] !== -1 || (L[s] >= T && alpha[s] > 8)) continue;
      let head = 0, tail = 0; q[tail++] = s; lab[s] = fields; let touches = false, area = 0;
      while (head < tail) {
        const p = q[head++]; area++; const px0 = p % w, py0 = (p / w) | 0;
        if (px0 === 0 || py0 === 0 || px0 === w - 1 || py0 === h - 1) touches = true;
        const nb = [p - 1, p + 1, p - w, p + w];
        for (let t = 0; t < 4; t++) {
          const v = nb[t]; if (v < 0 || v >= n) continue; if ((t === 0 && px0 === 0) || (t === 1 && px0 === w - 1)) continue;
          if (lab[v] !== -1 || (L[v] >= T && alpha[v] > 8)) continue; lab[v] = fields; q[tail++] = v;
        }
      }
      if (touches || area > bigField) for (let i = 0; i < tail; i++) m[q[i]] = 0;
      fields++;
    }
    // feathered: two passes of a 3×3 average soften the seam between the grade and the black
    let cur = m;
    for (let p = 0; p < 2; p++) {
      const nx = new Float32Array(n);
      for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
        let s = 0, c2 = 0; for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) { const a = i + di, b = j + dj; if (a < 0 || b < 0 || a >= w || b >= h) continue; s += cur[b * w + a]; c2++; }
        nx[j * w + i] = s / c2;
      }
      cur = nx;
    }
    return { w, h, m: cur, plate: true };
  }

  /* ---- the grade: one shared WebGL context ---- */
  const VS = 'attribute vec2 p; varying vec2 uv; void main(){ uv = p * 0.5 + 0.5; uv.y = 1.0 - uv.y; gl_Position = vec4(p, 0.0, 1.0); }';
  const FS = `precision highp float;
varying vec2 uv; uniform sampler2D img, mask; uniform vec2 px;
uniform float ex, b, c, hi, sh, wh, bl, temp, tint, vib, sat, hue, sharp, whole;
float lum(vec3 v){ return dot(v, vec3(0.2126, 0.7152, 0.0722)); }
float toLinear(float x){ return x <= 0.04045 ? x / 12.92 : pow((x + 0.055) / 1.055, 2.4); }
float toSrgb(float x){ return x <= 0.0031308 ? 12.92 * x : 1.055 * pow(x, 1.0 / 2.4) - 0.055; }
vec3 rotateHue(vec3 v, float a){
  float y = lum(v);
  float i = dot(v, vec3(0.596, -0.274, -0.322));
  float q = dot(v, vec3(0.211, -0.523, 0.312));
  float cs = cos(a), sn = sin(a);
  float ni = i * cs - q * sn, nq = i * sn + q * cs;
  return vec3(y + 0.956 * ni + 0.621 * nq, y - 0.272 * ni - 0.647 * nq, y - 1.106 * ni + 1.703 * nq);
}
vec3 tone(vec3 v){
  if (abs(ex) + abs(temp) + abs(tint) > 0.0001) {
    vec3 linear = vec3(toLinear(v.r), toLinear(v.g), toLinear(v.b));
    // Exposure is ±2 stops. Temperature/tint act as modest channel gains in linear light.
    linear *= exp2(ex * 2.0) * exp(vec3(temp * 0.32 + tint * 0.12, -tint * 0.25, -temp * 0.32 + tint * 0.12));
    v = vec3(toSrgb(linear.r), toSrgb(linear.g), toSrgb(linear.b));
  }
  v = pow(max(v, 0.0), vec3(exp2(-b)));                                   // brightness: a midtone gamma; black and white stay
  float L = lum(v), L1 = L;
  L1 = L1 + c * (L1 * L1 * (3.0 - 2.0 * L1) - L1);                          // contrast: an S-curve that keeps 0 and 1
  float wH = smoothstep(0.45, 1.0, L1); L1 += hi * 0.5 * wH * (hi > 0.0 ? 1.0 - L1 : L1);
  float wS = 1.0 - smoothstep(0.0, 0.55, L1); L1 += sh * 0.4 * wS * (sh > 0.0 ? 1.0 - L1 : L1);
  float wW = pow(smoothstep(0.62, 1.0, L1), 2.0); L1 += wh * 0.35 * wW * (wh > 0.0 ? 1.0 - L1 : L1);
  float wK = 1.0 - smoothstep(0.0, 0.22, L1); L1 += bl * 0.1 * wK;
  L1 = clamp(L1, 0.0, 1.0);
  // a bright tone keeps its colour by scaling it; a near-black one is lifted evenly — scaling a dark tone multiplies
  // the colour noise a compressed photograph hides in its darks (it turned a necklace's gaps purple)
  float keep = smoothstep(0.04, 0.18, L);
  v = mix(v + (L1 - L), v * (L1 / max(L, 1e-4)), keep);
  if (abs(hue) > 0.0001) v = rotateHue(v, hue * 3.14159265);
  float L2 = lum(v);
  float chroma = max(v.r, max(v.g, v.b)) - min(v.r, min(v.g, v.b));
  float protectDarks = smoothstep(0.03, 0.2, L2);
  float colorGain = 1.0 + (sat + vib * (1.0 - clamp(chroma, 0.0, 1.0)) * 0.8) * protectDarks;
  v = mix(vec3(L2), v, max(0.0, colorGain)); // vibrance favors muted color; saturation changes every color
  return clamp(v, 0.0, 1.0);
}
void main(){
  vec4 o = texture2D(img, uv);
  vec3 g = tone(o.rgb);
  if (sharp > 0.0) {                                                        // sharpness: unsharp mask on the graded picture
    vec3 n = texture2D(img, uv + vec2(px.x, 0.0)).rgb + texture2D(img, uv - vec2(px.x, 0.0)).rgb
           + texture2D(img, uv + vec2(0.0, px.y)).rgb + texture2D(img, uv - vec2(0.0, px.y)).rgb;
    vec3 edge = clamp(o.rgb - n * 0.25, -0.08, 0.08);
    g = clamp(g + sharp * edge * smoothstep(0.005, 0.03, length(edge)), 0.0, 1.0);
  }
  float m = whole > 0.5 ? 1.0 : texture2D(mask, uv).r;
  gl_FragColor = vec4(mix(o.rgb, g, m), o.a);
}`;
  let gl = null, prog = null, canvas = null, loc = null;
  function setup() {
    if (gl) return gl;
    canvas = document.createElement('canvas');
    gl = canvas.getContext('webgl', { premultipliedAlpha: false, preserveDrawingBuffer: true });
    if (!gl) return null;
    const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(o)); return o; };
    prog = gl.createProgram(); gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(prog); gl.useProgram(prog);
    const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const a = gl.getAttribLocation(prog, 'p'); gl.enableVertexAttribArray(a); gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0);
    loc = {}; for (const k of ['img', 'mask', 'px', 'ex', 'b', 'c', 'hi', 'sh', 'wh', 'bl', 'temp', 'tint', 'vib', 'sat', 'hue', 'sharp', 'whole']) loc[k] = gl.getUniformLocation(prog, k);
    return gl;
  }
  const texture = (unit, src, w, h, data) => {
    const t = gl.createTexture(); gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (src) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
    else gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, w, h, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, data);
    return t;
  };
  function render(source, mk, g, maxw) {
    if (!setup()) return null;
    const MAXW = Math.min(maxw || 1600, gl.getParameter(gl.MAX_TEXTURE_SIZE) || 4096); const k = Math.min(1, MAXW / Math.max(source.naturalWidth, source.naturalHeight));
    canvas.width = Math.round(source.naturalWidth * k); canvas.height = Math.round(source.naturalHeight * k);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    const t0 = texture(0, source);
    const bytes = new Uint8Array(mk.w * mk.h); for (let i = 0; i < bytes.length; i++) bytes[i] = Math.round(mk.m[i] * 255);
    const t1 = texture(1, null, mk.w, mk.h, bytes);
    gl.uniform1i(loc.img, 0); gl.uniform1i(loc.mask, 1); gl.uniform2f(loc.px, 1 / canvas.width, 1 / canvas.height);
    const v = (key) => (+g[key] || 0) / 100;
    gl.uniform1f(loc.ex, v('exposure')); gl.uniform1f(loc.b, v('brightness')); gl.uniform1f(loc.c, v('contrast')); gl.uniform1f(loc.hi, v('highlights')); gl.uniform1f(loc.sh, v('shadows'));
    gl.uniform1f(loc.wh, v('whites')); gl.uniform1f(loc.bl, v('blacks')); gl.uniform1f(loc.temp, v('temperature')); gl.uniform1f(loc.tint, v('tint'));
    gl.uniform1f(loc.vib, v('vibrance')); gl.uniform1f(loc.sat, v('saturation')); gl.uniform1f(loc.hue, v('hue'));
    gl.uniform1f(loc.sharp, v('sharpness') * 1.2); gl.uniform1f(loc.whole, g.whole ? 1 : 0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.deleteTexture(t0); gl.deleteTexture(t1);
    return canvas;
  }

  /* ---- a card's picture: the <img> the plate frames ---- */
  const pictureOf = (media) => media && media.querySelector('img.gg-card__img:not(.gg-card__img--alt), .gg-pdp__slide > img');
  const sourceCache = new Map();   // original url → { image, mask }; bounded to avoid keeping every decoded product in memory
  const load = (url) => new Promise((res, rej) => { const im = new Image(); im.crossOrigin = 'anonymous'; im.decoding = 'async'; im.onload = () => res(im); im.onerror = () => rej(new Error('the picture could not be read for grading')); im.src = url; });
  async function sourceFor(url) {
    if (!sourceCache.has(url)) sourceCache.set(url, load(url).then((image) => ({ image, mask: maskOf(image) })).catch((error) => { sourceCache.delete(url); throw error; }));
    const entry = sourceCache.get(url); sourceCache.delete(url); sourceCache.set(url, entry);
    while (sourceCache.size > 8) sourceCache.delete(sourceCache.keys().next().value);
    return entry;
  }
  /* ---- one picture, graded in place: the original src kept aside, the graded blob shown; no grade brings the original
     back exactly. Answers 'piece' (a black plate was found and kept) · 'whole' (none found: all of it graded) · 'off' ·
     'unread' (the picture could not be read) · null (a newer grade was asked for meanwhile, and wins). ---- */
  const jobs = new WeakMap();
  async function gradeImg(img, g, maxw) {
    const my = {}; jobs.set(img, my);                 // any grade still on its way to this picture is stale from now
    const orig = img.dataset.gradeSrc;
    if (!g) {                                        // no grade: the original, exactly
      if (orig) { img.src = orig; if (img.dataset.gradeSrcset) img.srcset = img.dataset.gradeSrcset; delete img.dataset.gradeSrc; delete img.dataset.gradeSrcset; }
      if (img.dataset.gradeBlob) { URL.revokeObjectURL(img.dataset.gradeBlob); delete img.dataset.gradeBlob; }
      return 'off';
    }
    const url = orig || img.currentSrc || img.src; if (!url) return 'unread';
    try {
      const { image, mask } = await sourceFor(url);
      if (jobs.get(img) !== my) return null;
      const out = render(image, mask, g, maxw); if (!out) return 'unread';
      const blob = await new Promise((r) => out.toBlob(r, 'image/webp', 0.94));
      if (jobs.get(img) !== my) return null;
      if (!blob) return 'unread';
      if (!img.dataset.gradeSrc) { img.dataset.gradeSrc = url; if (img.getAttribute('srcset')) img.dataset.gradeSrcset = img.getAttribute('srcset'); }
      img.removeAttribute('srcset');
      const was = img.dataset.gradeBlob; const next = URL.createObjectURL(blob);
      img.src = next; img.dataset.gradeBlob = next; if (was) setTimeout(() => URL.revokeObjectURL(was), 1000);
      return mask.plate ? 'piece' : 'whole';
    } catch (e) {
      console.warn('[plate grade]', e.message); return 'unread';
    }
  }
  const mark = (host, st) => {
    if (st === 'off') { host.classList.remove('is-graded'); delete host.dataset.gradeState; return true; }
    if (st === 'unread') { host.dataset.gradeState = 'unread'; return false; }
    host.classList.add('is-graded'); host.dataset.gradeState = st; return true;
  };
  async function apply(media, grade) {
    const img = pictureOf(media); if (!img) return false;
    const st = await gradeImg(img, clean(grade), 1600);
    return st === null ? false : mark(media, st);
  }

  /* ---- a FRAME's photograph: what the frame shows. A picture laid over the frame's own (the Cover Lab's
     .gg-lab__media--base) is what it shows. A frame that shows a film is not graded — a film is never graded, and its
     poster alone would jump when the film starts. The marks' little pictures and the Lab's own chrome are never the
     frame's. Graded at the size the frame needs on this screen, never beyond the picture's own. ---- */
  const NOT_FRAME = '.gg-lab, .gg-lab-hist, .gg-lab-frame, .gg-lab-trim, .gg-marks, .gg-mark, .gg-hotspot, .gg-back, [data-grade-skip]';
  const FRAME = '[data-lab-slot], [data-frame-grade]';
  function photosOf(frame) {
    const own = frame.querySelector(':scope > .gg-lab__media--base');
    if (own) return own.tagName === 'IMG' ? [own] : [];
    const mine = (n) => !n.closest(NOT_FRAME) && n.closest(FRAME) === frame;
    if ([...frame.querySelectorAll('video')].some(mine)) return [];
    return [...frame.querySelectorAll('img')].filter((i) => mine(i) && !i.classList.contains('gg-lab__media'));
  }
  const frameWidth = (frame) => { const r = frame.getBoundingClientRect(); return Math.min(3200, Math.max(1600, Math.round(Math.max(r.width, r.height * 1.25) * Math.min(2, window.devicePixelRatio || 1)))); };
  async function applyFrame(frame, grade) {
    const g = clean(grade); const w = frameWidth(frame);
    // a picture this frame no longer shows (the Lab laid its own over it, or took it away) goes back to its original
    const now = photosOf(frame); const was = frame.__gradeImgs || [];
    frame.__gradeImgs = now;
    was.filter((i) => !now.includes(i) && i.dataset.gradeSrc).forEach((i) => gradeImg(i, null));
    if (!now.length) { mark(frame, 'off'); return false; }
    const sts = await Promise.all(now.map((i) => gradeImg(i, g, w)));
    if (frame.__gradeImgs !== now || sts.includes(null)) return false;
    return mark(frame, sts.includes('unread') ? 'unread' : !g ? 'off' : sts.includes('piece') ? 'piece' : 'whole');
  }

  /* ---- on the page: every plate and every frame that carries a grade, graded as it comes near the screen. A frame the
     Cover Lab has taken over (data-grade-lab) is graded by the Lab alone, from what it holds. ---- */
  const parse = (s) => { try { return s ? JSON.parse(s) : null; } catch { return null; } };
  const SEL = '.gg-plate[data-plate-grade], [data-frame-grade]';
  const run = (el) => { if (el.matches('.gg-plate[data-plate-grade]')) apply(el, parse(el.dataset.plateGrade)); else if (!el.dataset.gradeLab) applyFrame(el, parse(el.dataset.frameGrade)); };
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { io.unobserve(e.target); run(e.target); } }), { rootMargin: '600px' }) : null;
  const scan = (root) => (root.querySelectorAll ? root.querySelectorAll(SEL) : []).forEach((m) => { if (m.dataset.gradeSeen) return; m.dataset.gradeSeen = '1'; if (io) io.observe(m); else run(m); });
  const start = () => { scan(document); new MutationObserver((rs) => { for (const r of rs) for (const n of r.addedNodes) if (n.nodeType === 1) { if (n.matches && n.matches(SEL)) scan(n.parentNode || document); else scan(n); } }).observe(document.body, { childList: true, subtree: true }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();

  window.ggGrade = { apply, applyFrame, photosOf, clean, isGraded, KEYS, maskOf };
})();
